import React, { useState, useEffect, useRef } from 'react';
import { useSetCurrentPage, useAppStore } from '../stores/useAppStore';
import { useToastStore } from '../stores/useToastStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import { accountService } from '../services/accountService';
import { ORDER_SIDE, POSITION_SIDE } from '@/services/enums';

interface PendingOrder {
  instId: string;
  ordId: string;
  clOrdId?: string;
  tag?: string;
  px?: string;
  sz?: string;
  pnl?: string;
  ordType: string;
  side: string;
  posSide?: string;
  tdMode: string;
  accFillSz?: string;
  fillPx?: string;
  tradeId?: string;
  fillSz?: string;
  fillTime?: string;
  avgPx?: string;
  state: string;
  lever?: string;
  tpTriggerPx?: string;
  tpOrdPx?: string;
  slTriggerPx?: string;
  slOrdPx?: string;
  feeCcy?: string;
  fee?: string;
  rebateCcy?: string;
  rebate?: string;
  tgtCcy?: string;
  cTime: string;
  uTime: string;
  reqId?: string;
  amendResult?: string;
  code?: string;
  msg?: string;
  usdtAmount?: string | null; // 后端计算好的USDT金额
}

const MyPendingOrders: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const setPlaybackSymbol = useAppStore((state) => state.setPlaybackSymbol);
  const showToast = useToastStore((state) => state.show);
  const [orders, setOrders] = useState<PendingOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  // 加载未成交订单列表
  const loadPendingOrders = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      const response = await accountService.getPendingOrders();

      // response IS the data (array of pending orders)
      setOrders(response || []);
    } catch (error: any) {
      console.error('获取未成交订单失败:', error);
      const message =
        error?.response?.status === 403
          ? error.message || '权限不足，无法查看未成交订单'
          : error.message || '获取未成交订单失败';
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  // 取消订单
  const cancelOrder = async (ordId: string, _instId: string) => {
    if (cancellingOrderId) return; // 防止重复点击

    try {
      setCancellingOrderId(ordId);
      // 这里需要调用取消订单的API，暂时先提示
      showToast('取消订单功能开发中', 'info');
    } catch (error: any) {
      console.error('取消订单失败:', error);
      showToast(error.message || '取消订单失败', 'error');
    } finally {
      setCancellingOrderId(null);
    }
  };

  useEffect(() => {
    // 防止重复加载（React StrictMode 会执行两次）
    if (hasLoadedRef.current) {
      return;
    }

    hasLoadedRef.current = true;
    loadPendingOrders();
  }, []);

  const formatDate = (timestamp: string) => {
    const date = new Date(parseInt(timestamp));
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatPrice = (price?: string) => {
    if (!price) return '-';
    return parseFloat(price).toFixed(8);
  };

  const formatSize = (size?: string) => {
    if (!size) return '-';
    return parseFloat(size).toFixed(4);
  };

  const formatFee = (fee?: string) => {
    if (!fee) return '-';
    return parseFloat(fee).toFixed(6);
  };

  const formatUSDT = (usdt?: string) => {
    if (!usdt) return '-';
    return parseFloat(usdt).toFixed(2);
  };

  const getOrderTypeText = (ordType: string) => {
    const typeMap: { [key: string]: string } = {
      market: '市价',
      limit: '限价',
      post_only: '只做maker',
      fok: '全部成交或立即取消',
      ioc: '立即成交并取消剩余',
      optimal_limit_ioc: '市价委托立即成交并取消剩余',
    };
    return typeMap[ordType] || ordType;
  };

  const getSideText = (side: string) => {
    return side === ORDER_SIDE.BUY ? '做多' : '做空';
  };

  const getPosSideText = (posSide?: string) => {
    if (!posSide) return '';
    return posSide === POSITION_SIDE.LONG ? '做多' : '做空';
  };

  const getStateText = (state: string) => {
    const stateMap: { [key: string]: string } = {
      live: '待成交',
      partially_filled: '部分成交',
      filled: '已成交',
      canceled: '已撤销',
    };
    return stateMap[state] || state;
  };

  const getTdModeText = (tdMode: string) => {
    const modeMap: { [key: string]: string } = {
      isolated: '逐仓',
      cross: '全仓',
      cash: '现金',
    };
    return modeMap[tdMode] || tdMode;
  };

  // 跳转到指标分析页面
  const handleViewMetrics = (instId: string) => {
    // 记录当前页面，用于返回
    const currentPage = useAppStore.getState().currentPage;
    sessionStorage.setItem('coin_metrics_previous_page', currentPage);

    // 直接使用instId作为参数
    setPlaybackSymbol(instId);
    setCurrentPage('coin-metrics-analysis');
  };

  return (
    <GradientBackground>
      <ParticleBackground />
      <div className="min-h-screen">
        {/* 页面头部 */}
        <PageHeader
          title="我的委托"
          onBack={() => setCurrentPage('personal-center')}
          rightContent={
            <button
              onClick={loadPendingOrders}
              disabled={loading}
              className="group flex items-center justify-center w-9 h-9 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:scale-105 disabled:opacity-50"
            >
              <svg
                className={`w-4 h-4 transition-transform duration-300 ${loading ? 'animate-spin' : 'group-hover:rotate-180'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          }
        />

        {/* 内容区域 */}
        <div className="px-4 py-4 pt-20">
          {errorMessage && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
              <div className="text-red-300 text-sm">{errorMessage}</div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-2">暂无未成交订单</div>
              <div className="text-gray-500 text-sm">所有未成交订单将显示在这里</div>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div
                  key={order.ordId}
                  className={`p-4 rounded-xl border backdrop-blur-sm ${
                    order.side === ORDER_SIDE.BUY
                      ? 'bg-green-500/10 border-green-500/30'
                      : 'bg-red-500/10 border-red-500/30'
                  }`}
                >
                  {/* 第一行：币种名称和按钮 */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-semibold text-lg">{order.instId}</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewMetrics(order.instId)}
                        className="px-3 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-sm transition-colors"
                        title="查看指标"
                      >
                        指标
                      </button>
                      {order.state === 'live' && (
                        <button
                          onClick={() => cancelOrder(order.ordId, order.instId)}
                          disabled={cancellingOrderId === order.ordId}
                          className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {cancellingOrderId === order.ordId ? '取消中...' : '取消'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* 第二行：标签（做多、做空、待成交、订单类型、交易模式、杠杆倍数等） */}
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        order.side === ORDER_SIDE.BUY
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {getSideText(order.side)}
                    </span>
                    {order.posSide && (
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-500/20 text-blue-400">
                        {getPosSideText(order.posSide)}
                      </span>
                    )}
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-purple-500/20 text-purple-400">
                      {getOrderTypeText(order.ordType)}
                    </span>
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-500/20 text-yellow-400">
                      {getTdModeText(order.tdMode)}
                    </span>
                    {order.lever && (
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-indigo-500/20 text-indigo-400">
                        {order.lever}x
                      </span>
                    )}
                    <span className="px-2 py-1 rounded text-xs bg-gray-500/20 text-gray-400">
                      {getStateText(order.state)}
                    </span>
                  </div>

                  {/* 订单详情 */}
                  <div className="space-y-2 text-sm text-white/80">
                    {/* 委托价格和委托数量(USDT)在同一行 */}
                    {(order.px || order.usdtAmount) && (
                      <div className="flex items-center gap-4">
                        {order.px && (
                          <div className="flex items-center">
                            <span className="text-white/60 whitespace-nowrap">委托价格:</span>
                            <span className="text-white ml-2 whitespace-nowrap">{formatPrice(order.px)}</span>
                          </div>
                        )}
                        {order.usdtAmount && (
                          <div className="flex items-center">
                            <span className="text-white/60 whitespace-nowrap">委托数量(USDT):</span>
                            <span className="text-white ml-2 whitespace-nowrap">{formatUSDT(order.usdtAmount)}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 平均成交价 */}
                    {order.avgPx && (
                      <div className="flex items-center">
                        <span className="text-white/60">平均成交价:</span>
                        <span className="text-white ml-2">{formatPrice(order.avgPx)}</span>
                      </div>
                    )}

                    {/* 累计成交数量和手续费在同一行，左对齐 */}
                    {(order.accFillSz || order.fee) && (
                      <div className="flex items-center gap-4">
                        {order.accFillSz && (
                          <div className="flex items-center">
                            <span className="text-white/60 whitespace-nowrap">累计成交数量:</span>
                            <span className="text-white ml-2 whitespace-nowrap">{formatSize(order.accFillSz)}</span>
                          </div>
                        )}
                        {order.fee && (
                          <div className="flex items-center">
                            <span className="text-white/60 whitespace-nowrap">手续费:</span>
                            <span className="text-white ml-2 whitespace-nowrap">{formatFee(order.fee)}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 止盈止损 */}
                    {(order.tpTriggerPx || order.slTriggerPx) && (
                      <div className="mt-2 pt-2 border-t border-white/10">
                        <div className="font-semibold mb-1 text-white">止盈止损:</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {order.tpTriggerPx && (
                            <div>
                              <span className="text-white/60">止盈触发价:</span>
                              <span className="text-green-400 ml-2">{formatPrice(order.tpTriggerPx)}</span>
                            </div>
                          )}
                          {order.tpOrdPx && (
                            <div>
                              <span className="text-white/60">止盈委托价:</span>
                              <span className="text-green-400 ml-2">{formatPrice(order.tpOrdPx)}</span>
                            </div>
                          )}
                          {order.slTriggerPx && (
                            <div>
                              <span className="text-white/60">止损触发价:</span>
                              <span className="text-red-400 ml-2">{formatPrice(order.slTriggerPx)}</span>
                            </div>
                          )}
                          {order.slOrdPx && (
                            <div>
                              <span className="text-white/60">止损委托价:</span>
                              <span className="text-red-400 ml-2">{formatPrice(order.slOrdPx)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 最下面一行：订单ID和时间 */}
                    <div className="flex items-center justify-between text-xs text-white/60 mt-3 pt-2 border-t border-white/10">
                      <span>订单ID: {order.ordId}</span>
                      <span>{formatDate(order.cTime)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </GradientBackground>
  );
};

export default MyPendingOrders;
